import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';
import { ImageUploader } from '../../components/ImageUploader';

interface Company {
  id: string;
  name: string;
  logo: string | null;
  cr: string | null;
  falLicense: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  isHouse: boolean;
  active: boolean;
  _count?: { associations: number; managers: number };
}

interface ManagerProfile {
  userId: string;
  licenseNumber: string | null;
  licenseExpires: string | null;
  yearsExperience: number;
  totalAssociations: number;
  active: boolean;
  user: { id: string; name: string; email: string | null; phone: string | null; image: string | null };
  company: { id: string; name: string };
}

export function AdminCompaniesPage() {
  const [tab, setTab] = useState<'companies' | 'managers'>('companies');
  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-6">🏢 شركات الإدارة العقارية</h1>
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <Tab active={tab === 'companies'} onClick={() => setTab('companies')}>🏢 الشركات</Tab>
        <Tab active={tab === 'managers'}  onClick={() => setTab('managers')}>👨‍💼 مديرو العقار</Tab>
      </div>
      {tab === 'companies' ? <CompaniesList /> : <ManagersList />}
    </div>
  );
}

function CompaniesList() {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);

  const { data } = useQuery({
    queryKey: ['management-companies'],
    queryFn: () => api.get<ApiSuccess<Company[]>>('/api/admin/management-companies'),
  });
  const toggleM = useMutation({
    mutationFn: (id: string) => api.put(`/api/admin/management-companies/${id}/toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management-companies'] }),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/management-companies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['management-companies'] }),
  });

  const list = data?.data ?? [];

  return (
    <>
      <div className="flex justify-end mb-4"><Button variant="primary" onClick={() => setOpenCreate(true)}>+ شركة جديدة</Button></div>
      {list.length === 0 ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد شركات</div></CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((c) => (
            <Card key={c.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-3">
                    {c.logo ? <img src={c.logo} className="w-12 h-12 rounded object-cover" /> : <div className="w-12 h-12 rounded bg-navy-dark text-white flex items-center justify-center text-xl">🏢</div>}
                    <div>
                      <h3 className="font-black text-navy-dark text-lg">{c.name}</h3>
                      <div className="flex gap-1 mt-1">
                        {c.isHouse && <Badge color="gold">شركتنا</Badge>}
                        <Badge color={c.active ? 'green' : 'red'}>{c.active ? 'نشطة' : 'موقوفة'}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                {c.cr && <div className="text-xs text-gray-500" dir="ltr">س.ت: {c.cr}</div>}
                {c.falLicense && <div className="text-xs text-gray-500" dir="ltr">فال: {c.falLicense}</div>}
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div className="bg-gray-50 rounded p-2"><div className="text-gray-500">الجمعيات</div><div className="font-bold">{c._count?.associations ?? 0}</div></div>
                  <div className="bg-gray-50 rounded p-2"><div className="text-gray-500">المديرون</div><div className="font-bold">{c._count?.managers ?? 0}</div></div>
                </div>
                <div className="flex gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Button size="sm" variant="primary" onClick={() => setEditing(c)} className="flex-1">✏️ تعديل</Button>
                  <Button size="sm" variant={c.active ? 'ghost' : 'primary'} onClick={() => toggleM.mutate(c.id)}>{c.active ? '⏸' : '▶'}</Button>
                  {!c.isHouse && <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف ${c.name}؟`)) deleteM.mutate(c.id); }}>🗑</Button>}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {(openCreate || editing) && <CompanyEditModal company={editing} onClose={() => { setOpenCreate(false); setEditing(null); }} onSaved={() => qc.invalidateQueries({ queryKey: ['management-companies'] })} />}
    </>
  );
}

function CompanyEditModal({ company, onClose, onSaved }: { company: Company | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    name: company?.name ?? '',
    logo: company?.logo ?? '',
    cr: company?.cr ?? '',
    falLicense: company?.falLicense ?? '',
    email: company?.email ?? '',
    phone: company?.phone ?? '',
    website: company?.website ?? '',
    isHouse: company?.isHouse ?? false,
    active: company?.active ?? true,
  });
  const [err, setErr] = useState('');
  const m = useMutation({
    mutationFn: () => company ? api.put(`/api/admin/management-companies/${company.id}`, f) : api.post('/api/admin/management-companies', f),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">{company ? '✏️ تعديل' : '➕ شركة جديدة'}</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="اسم الشركة *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <div>
            <ImageUploader
              value={f.logo}
              onChange={(url) => setF({ ...f, logo: url ?? '' })}
              purpose="COMPANY_LOGO"
              label="🏢 لوقو الشركة"
              hint="مربع — حد أقصى 2MB"
              shape="square"
              size="md"
            />
          </div>
          <Input label="السجل التجاري" value={f.cr} onChange={(e) => setF({ ...f, cr: e.target.value })} dir="ltr" />
          <Input label="ترخيص فال" value={f.falLicense} onChange={(e) => setF({ ...f, falLicense: e.target.value })} dir="ltr" />
          <Input label="الإيميل" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} dir="ltr" />
          <Input label="الجوال" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} dir="ltr" />
          <Input label="الموقع" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} dir="ltr" />
          <div className="flex flex-col gap-2 mt-7">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.isHouse} onChange={(e) => setF({ ...f, isHouse: e.target.checked })} className="w-5 h-5 accent-gold" /><span className="text-sm font-bold">⭐ شركتنا (الافتراضية)</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-5 h-5 accent-green-600" /><span className="text-sm font-bold">نشطة</span></label>
          </div>
        </div>
        {err && <div className="mx-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.name}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}

function ManagersList() {
  const qc = useQueryClient();
  const [openAdd, setOpenAdd] = useState(false);
  const { data } = useQuery({
    queryKey: ['property-managers'],
    queryFn: () => api.get<ApiSuccess<ManagerProfile[]>>('/api/admin/management-companies/managers/list'),
  });
  const deleteM = useMutation({
    mutationFn: (userId: string) => api.delete(`/api/admin/management-companies/managers/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['property-managers'] }),
  });
  const list = data?.data ?? [];

  return (
    <>
      <div className="flex justify-end mb-4"><Button variant="primary" onClick={() => setOpenAdd(true)}>+ تسجيل مدير</Button></div>
      {list.length === 0 ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">لا يوجد مديرون</div></CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((m) => (
            <Card key={m.userId}>
              <CardBody>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-navy-dark text-white flex items-center justify-center text-xl">👨‍💼</div>
                    <div>
                      <div className="font-black text-navy-dark">{m.user.name}</div>
                      <div className="text-xs text-gray-500" dir="ltr">{m.user.email ?? m.user.phone}</div>
                      <Badge color="navy">{m.company.name}</Badge>
                    </div>
                  </div>
                  <Badge color={m.active ? 'green' : 'red'}>{m.active ? 'نشط' : 'موقوف'}</Badge>
                </div>
                <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                  {m.licenseNumber && <div dir="ltr">ترخيص: {m.licenseNumber}</div>}
                  <div>عدد الجمعيات: <b>{m.totalAssociations}</b></div>
                  <div>سنوات الخبرة: <b>{m.yearsExperience}</b></div>
                </div>
                <div className="flex gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف المدير؟')) deleteM.mutate(m.userId); }}>🗑 حذف</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {openAdd && <AddManagerModal onClose={() => setOpenAdd(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['property-managers'] })} />}
    </>
  );
}

function AddManagerModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ userId: '', companyId: '', licenseNumber: '', yearsExperience: '0', bio: '' });
  const [err, setErr] = useState('');
  const { data: companies } = useQuery({
    queryKey: ['management-companies-list'],
    queryFn: () => api.get<ApiSuccess<Company[]>>('/api/admin/management-companies'),
  });
  const m = useMutation({
    mutationFn: () => api.post('/api/admin/management-companies/managers', { ...f, yearsExperience: Number(f.yearsExperience) }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">+ تسجيل مدير عقار</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <Input label="معرّف المستخدم *" value={f.userId} onChange={(e) => setF({ ...f, userId: e.target.value })} dir="ltr" hint="انسخ ID من صفحة المستخدمين" />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الشركة *</label>
            <select value={f.companyId} onChange={(e) => setF({ ...f, companyId: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
              <option value="">— اختر —</option>
              {companies?.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="رقم الترخيص (فال)" value={f.licenseNumber} onChange={(e) => setF({ ...f, licenseNumber: e.target.value })} dir="ltr" />
            <Input label="سنوات الخبرة" type="number" value={f.yearsExperience} onChange={(e) => setF({ ...f, yearsExperience: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">نبذة</label>
            <textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={3} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
          </div>
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.userId || !f.companyId}>تسجيل</Button>
        </div>
      </div>
    </div>
  );
}

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}
